import React from 'react';
import { motion } from 'motion/react';
import {
  Youtube,
  Instagram,
  Rocket,
  Sparkles,
  MessageSquare,
  Code2,
  ArrowRight,
  Zap,
  Activity,
  Cpu,
  Clock,
  Layers,
  Music
} from 'lucide-react';
import SEO from '@/components/SEO';
import { BackgroundAnimation } from '@/components/background-animation';
import { Link } from 'react-router-dom';
import InternalLinksSection from '@/components/InternalLinksSection';

const About: React.FC = () => {


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] }
    }
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Fun Circle Games",
    "description": "Learn about Fun Circle Games, the creator studio for satisfying ball simulations and viral physics content. Built by a single founder with 8+ years of game development experience.",
    "publisher": {
      "@type": "Organization",
      "name": "Fun Circle Games",
      "logo": {
        "@type": "ImageObject",
        "url": "https://funcirclegames.com/logo.png"
      }
    }
  };

  return (
    <div className="relative min-h-screen pt-20 overflow-x-hidden bg-white text-zinc-900 selection:bg-cyan-500/20">
      <SEO
        title="About Fun Circle Games – The Physics Video Generator Brand"
        description="Learn about Fun Circle Games, the creator platform for satisfying ball simulations and viral physics content. Discover our mission to empower faceless creators."
        keywords="About Fun Circle Games, Fun Circle Games mission, Fun Circle Games founder, physics video generator brand, Creator Studio history"
        schema={aboutSchema}
      />

      <BackgroundAnimation opacity={0.3} />

      {/* Hero Section */}
      <section className="relative px-6 pt-24 pb-16">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] }}
            className="space-y-6"
          >
            <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-cyan-600 mb-2">About the Brand</h2>
            <h1 className="text-5xl md:text-8xl font-display font-black tracking-tighter leading-[0.85] uppercase">
              FUN CIRCLE <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-purple-600 to-cyan-600">GAMES STUDIO</span>
            </h1>
            <p className="text-xl md:text-2xl text-zinc-500 font-medium max-w-3xl mx-auto italic font-serif leading-relaxed">
              "Fun Circle Games is more than just software—it's a creator platform built to revolutionize how satisfying physics content is made."
            </p>
          </motion.div>
        </div>
      </section>

      {/* Brand Identity / Entity Information */}
      <section className="py-20 px-6 relative overflow-hidden bg-zinc-50 border-y border-zinc-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

            {/* Identity Info */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-7 space-y-8"
            >
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 border border-cyan-100 text-xs font-bold text-cyan-600 uppercase tracking-wider">
                  <Activity className="w-3.5 h-3.5" /> Our Identity
                </div>
                <h2 className="text-4xl md:text-5xl font-display font-black tracking-tighter leading-tight uppercase">
                  WHAT IS <br />
                  <span className="text-cyan-600 italic">FUN CIRCLE GAMES?</span>
                </h2>
              </div>

              <div className="space-y-6 text-base md:text-lg text-zinc-600 leading-relaxed">
                <p>
                  <span className="text-zinc-950 font-bold">Fun Circle Games</span> is the industry-leading physics video generator designed specifically for short-form content creators. We specialize in "oddly satisfying" simulations that dominate social media algorithms.
                </p>
                <p>
                  As a dedicated <span className="text-zinc-950 font-bold">Creator Studio</span>, we provide a no-code interface for building complex rhythmic and physics-based animations. Our goal is to enable anyone to become a high-retention content creator without needing a degree in physics or years of code experience.
                </p>
                <p>
                  Every single update, optimization, and feature in this platform is developed by a single founder, ensuring a clean, focused, and high-performance codebase optimized for creators worldwide.
                </p>
              </div>
            </motion.div>

            {/* Side Card: The Founder Logo & Small Detail */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="lg:col-span-5"
            >
              <div className="p-8 rounded-[2rem] bg-white border border-zinc-200 shadow-xl space-y-6 relative overflow-hidden group">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,229,255,0.02),transparent_70%)]" />

                <h3 className="text-xl font-display font-black uppercase tracking-tight text-zinc-950">Development & Engineering</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">
                  Fun Circle Games is engineered from the ground up for high-retention, viral physics content with zero technical hurdles.
                </p>

                {/* Minimalist Founder Detail (Small & Non-Obsessive) */}
                <div className="flex items-center gap-4 p-4 rounded-2xl bg-zinc-50 border border-zinc-150 relative z-10">
                  <div className="relative w-16 h-16 rounded-full overflow-hidden border border-zinc-200 bg-zinc-100 flex-shrink-0 flex items-center justify-center">
                    <picture className="w-full h-full">
                      <source srcSet="/founderlogo.webp" type="image/webp" />
                      <source srcSet="/founderlogo.png" type="image/png" />
                      <img
                        src="/founderlogo.jpg"
                        alt="Founder"
                        className="w-full h-full object-cover"
                      />
                    </picture>
                  </div>
                  <div className="space-y-1">
                    <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-zinc-400">Founder</h4>
                    <div className="text-sm font-bold text-zinc-950">Harshal</div>
                    <div className="text-[11px] text-zinc-500 font-mono">
                      8+ Years Exp.
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* The Story Timeline */}
      <section className="py-24 px-6 relative bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-cyan-600 mb-4 font-mono">The Journey</h2>
            <h3 className="text-4xl md:text-5xl font-display font-black uppercase tracking-tight text-zinc-950">
              HOW IT STARTED
            </h3>
          </div>

          <div className="relative border-l border-zinc-200 pl-8 md:pl-12 space-y-20 max-w-4xl mx-auto">
            {/* Pulsing Central Line Decoration */}
            <div className="absolute top-0 bottom-0 left-0 w-[2px] bg-gradient-to-b from-cyan-500 to-transparent pointer-events-none" />

            {/* Chapter 1 */}
            <motion.div
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="relative group"
            >
              <div className="absolute -left-[41px] md:-left-[57px] top-1.5 w-[18px] h-[18px] rounded-full border-4 border-white bg-cyan-500 shadow-md group-hover:scale-125 transition-transform" />

              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                <div className="md:col-span-4">
                  <h4 className="text-3xl font-display font-black opacity-15 leading-none">01</h4>
                  <div className="mt-[-0.5rem] space-y-1">
                    <span className="text-xs font-mono text-cyan-600 font-bold uppercase tracking-widest">January 10th, 2026</span>
                    <h5 className="text-lg font-bold tracking-tight text-zinc-950 uppercase">The Fun Start</h5>
                  </div>
                </div>
                <div className="md:col-span-8">
                  <div className="p-6 md:p-8 rounded-3xl border border-zinc-200 bg-zinc-50/50 hover:bg-zinc-50 transition-colors">
                    <p className="text-zinc-600 leading-relaxed text-sm md:text-base">
                      The founder started a channel on YouTube just for fun. The goal was simple: share simulations built using custom code. The founder loved watching physics interact and thought maybe others would too. There was no brand and no SaaS roadmap, just a single developer sharing scripts.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Chapter 2 */}
            <motion.div
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="relative group"
            >
              <div className="absolute -left-[41px] md:-left-[57px] top-1.5 w-[18px] h-[18px] rounded-full border-4 border-white bg-purple-500 shadow-md group-hover:scale-125 transition-transform" />

              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                <div className="md:col-span-4">
                  <h4 className="text-3xl font-display font-black opacity-15 leading-none">02</h4>
                  <div className="mt-[-0.5rem] space-y-1">
                    <span className="text-xs font-mono text-purple-600 font-bold uppercase tracking-widest font-sans">Going Viral</span>
                    <h5 className="text-lg font-bold tracking-tight text-zinc-950 uppercase">The Shift</h5>
                  </div>
                </div>
                <div className="md:col-span-8">
                  <div className="p-6 md:p-8 rounded-3xl border border-zinc-200 bg-zinc-50/50 hover:bg-zinc-50 transition-colors relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-6 opacity-5">
                      <Instagram className="w-20 h-20" />
                    </div>
                    <p className="text-zinc-600 leading-relaxed text-sm md:text-base">
                      Continuous uploads to YouTube led to Instagram. Suddenly, the project wasn't just a personal sandbox anymore. Thousands of people started watching, and more importantly, they started asking: <span className="text-zinc-950 font-bold italic">"How did you make this?"</span>
                    </p>
                    <div className="flex gap-3 mt-5 font-mono text-[10px]">
                      <span className="px-3 py-1.5 rounded-full border border-zinc-200 bg-white text-zinc-500 flex items-center gap-1.5 shadow-sm">
                        <MessageSquare className="w-3.5 h-3.5 text-cyan-600" /> "Tutorial?"
                      </span>
                      <span className="px-3 py-1.5 rounded-full border border-zinc-200 bg-white text-zinc-500 flex items-center gap-1.5 shadow-sm">
                        <Code2 className="w-3.5 h-3.5 text-purple-600" /> "What Engine?"
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Chapter 3 */}
            <motion.div
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="relative group"
            >
              <div className="absolute -left-[41px] md:-left-[57px] top-1.5 w-[18px] h-[18px] rounded-full border-4 border-white bg-zinc-950 shadow-md group-hover:scale-125 transition-transform" />

              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                <div className="md:col-span-4">
                  <h4 className="text-3xl font-display font-black opacity-15 leading-none">03</h4>
                  <div className="mt-[-0.5rem] space-y-1">
                    <span className="text-xs font-mono text-zinc-800 font-bold uppercase tracking-widest">The Evolution</span>
                    <h5 className="text-lg font-bold tracking-tight text-zinc-950 uppercase">The SaaS Vision</h5>
                  </div>
                </div>
                <div className="md:col-span-8">
                  <div className="p-6 md:p-8 rounded-3xl border border-zinc-200 bg-zinc-50/50 hover:bg-zinc-50 transition-colors relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-6 opacity-5">
                      <Rocket className="w-20 h-20" />
                    </div>
                    <p className="text-zinc-600 leading-relaxed text-sm md:text-base">
                      That was the turning point. Why not share this idea? The founder decided to build a tool that lets anyone create these simulations without needing to write complex code. That's when Fun Circle Games officially shifted from a social media channel to a SaaS platform.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* The Mission Section - High Impact */}
      <section className="py-24 bg-zinc-50 border-y border-zinc-150 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-7 space-y-8"
            >
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-100/60 border border-cyan-200 text-xs font-bold text-cyan-800 uppercase tracking-wider">
                  <Sparkles className="w-3.5 h-3.5" /> Our Mission
                </div>
                <h2 className="text-4xl md:text-5xl font-display font-black tracking-tighter leading-none uppercase text-zinc-950">
                  REVOLUTIONIZING <br />
                  <span className="text-cyan-600 italic">FACELESS CONTENT</span>
                </h2>
              </div>
              <p className="text-lg text-zinc-600 leading-relaxed">
                We are on a journey to democratize high-end physics simulations. Five months in, and we're just getting started. Our mission is to provide the engine that powers the next generation of digital creators.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-zinc-200">
                  <div className="w-10 h-10 rounded-xl bg-cyan-50 border border-cyan-100 flex items-center justify-center text-cyan-600">
                    <Rocket className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-zinc-950 uppercase tracking-tight">Future Modes</h4>
                    <p className="text-xs text-zinc-500">Continuously adding new ways to simulate and create.</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-zinc-200">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center text-purple-600">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-zinc-950 uppercase tracking-tight">Engine Evolution</h4>
                    <p className="text-xs text-zinc-500">Faster rendering, smarter physics, better results.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Engineering Highlights */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="lg:col-span-5 space-y-4"
            >
              <div className="p-6 rounded-2xl bg-white border border-zinc-200 space-y-3">
                <div className="flex items-center gap-2 text-cyan-600">
                  <Cpu className="w-5 h-5" />
                  <h4 className="font-bold text-sm text-zinc-950 uppercase tracking-wider">Decoupled Rendering</h4>
                </div>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Simulation loops are strictly isolated from the React lifecycle to maintain high 60+ FPS performance regardless of dashboard UI complexity.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-white border border-zinc-200 space-y-3">
                <div className="flex items-center gap-2 text-purple-600">
                  <Layers className="w-5 h-5" />
                  <h4 className="font-bold text-sm text-zinc-950 uppercase tracking-wider">Multithreaded Physics</h4>
                </div>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Heavy physics and rendering calculations are offloaded to Web Workers using Transferable Objects to prevent UI blocking.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-white border border-zinc-200 space-y-3">
                <div className="flex items-center gap-2 text-pink-600">
                  <Music className="w-5 h-5" />
                  <h4 className="font-bold text-sm text-zinc-950 uppercase tracking-wider">Precision Sound Sync</h4>
                </div>
                <p className="text-xs text-zinc-500 leading-relaxed">
                  Custom sound triggers built on top of Tone.js synchronize collision physics with midi events down to the exact millisecond.
                </p>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Founder's Sign-off */}
      <section className="py-28 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center space-y-10">
          <blockquote className="text-3xl md:text-4xl font-serif italic text-zinc-950 leading-tight">
            "We aren't just building software; we're building a new way for creators to express themselves. The future of faceless content is rhythmic, complex, and beautiful."
          </blockquote>

          <div className="space-y-1">
            <p className="font-display text-lg font-bold uppercase tracking-widest text-zinc-950">The Founder</p>
            <p className="text-xs text-zinc-400 font-mono uppercase tracking-widest">Fun Circle Games Studio</p>
          </div>

          <div className="pt-8">
            <a
              href="/dashboard"
              className="group inline-flex items-center gap-3 bg-zinc-950 text-white hover:bg-zinc-900 px-8 py-4.5 rounded-2xl font-bold text-md transition-all active:scale-[0.98] shadow-md"
            >
              Start Creating Now
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* Useful Links & Community Support */}
      <section className="py-16 px-6 bg-zinc-50 border-t border-zinc-150">
        <div className="max-w-4xl mx-auto space-y-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4 text-left">
              <h3 className="text-xl font-bold font-display uppercase tracking-tight text-zinc-950">Useful Links</h3>
              <p className="text-zinc-500 text-sm font-medium">
                Find pricing details, browse the blog, read FAQs, or reach out to support.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Link to="/plans" className="p-3 bg-white border border-zinc-200 hover:border-cyan-600 rounded-xl text-xs font-bold text-zinc-700 hover:text-cyan-600 uppercase tracking-widest text-center transition-all">Pricing</Link>
                <Link to="/blog" className="p-3 bg-white border border-zinc-200 hover:border-cyan-600 rounded-xl text-xs font-bold text-zinc-700 hover:text-cyan-600 uppercase tracking-widest text-center transition-all">Blog</Link>
                <Link to="/faq" className="p-3 bg-white border border-zinc-200 hover:border-cyan-600 rounded-xl text-xs font-bold text-zinc-700 hover:text-cyan-600 uppercase tracking-widest text-center transition-all">FAQ</Link>
                <Link to="/contact" className="p-3 bg-white border border-zinc-200 hover:border-cyan-600 rounded-xl text-xs font-bold text-zinc-700 hover:text-cyan-600 uppercase tracking-widest text-center transition-all">Contact</Link>
              </div>
            </div>

            <div className="p-6 rounded-[2rem] bg-white border border-zinc-200 shadow-lg text-left flex flex-col justify-between group h-full">
              <div className="space-y-4">
                <div className="w-10 h-10 rounded-xl bg-[#5865F2]/10 text-[#5865F2] flex items-center justify-center group-hover:bg-[#5865F2] group-hover:text-white transition-all">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <h4 className="font-bold text-zinc-950 uppercase tracking-tight text-sm">Discord Community</h4>
                <p className="text-xs text-zinc-500 leading-relaxed font-sans font-medium">
                  Join our Discord server. Ask bug questions, request features, or share your satisfying ball animations with fellow creators.
                </p>
              </div>
              <div className="pt-6">
                <a
                  href="https://discord.gg/cdwNFdbx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-[#5865F2] hover:text-[#4752C4]"
                >
                  Join Server <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <InternalLinksSection />

      {/* Footer Decoration */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-zinc-200 to-transparent opacity-50" />
    </div>
  );
};

export default About;
